// Shipping: the single source of truth for what a customer is CHARGED for
// shipping, plus the dealer's real FREIGHT COST (the dealer bears freight —
// autoplan premise gate 2026-05-29). Pure + deterministic so it can be
// unit-tested in isolation and inlined into the Vercel serverless targets
// (storefront checkout route + portal stripe-webhook) that cannot import
// workspace packages from source.
//
// Two numbers, deliberately independent:
//   • charged    — what the customer pays. $300 × set quantity when the cart
//                  contains an eFoil set (accessories ride free); the store's
//                  flat accessory rate (default $30, see FLAT_ACCESSORY_RATE)
//                  for an accessory-only cart; $0 when the customer pays by
//                  bank (ACH) and the dealer enabled free-ACH shipping; $0
//                  beyond the continental US, where the rates do not apply and
//                  the dealer quotes the real cost to the buyer after the sale.
//   • freightCost — the dealer's real freight obligation = Σ(unitShipping ×
//                  quantity) across all lines, ALWAYS (even when charged is 0).
//                  The order insight panel shows "Dealer-covered freight" =
//                  freightCost − charged (what the dealer absorbed on a waive).
//
// $300/set is NOT hardcoded here — it arrives as the set line's `unitShipping`
// (products.default_shipping), so freight is data-driven per SKU.
//
// CANONICAL COPY. computeShipping is inlined into apps/portal/api/stripe-webhook.ts
// (Vercel bundle constraint — CLAUDE.md "Workspace packages don't bundle in
// serverless functions"); keep that copy's behavior identical on every edit —
// guarded by apps/portal/tests/unit/shippingInline.test.ts. The storefront
// imports this module directly (through priceOrder and the cart drawer).

export type ShippingItemKind = 'set' | 'accessory' | 'other'

export interface ShippingInput {
  /** Stable line id, echoed into the breakdown so the panel can label rows. */
  id: string
  kind: ShippingItemKind
  /** SKU/bundle default_shipping (per unit). Null coerces to 0 at the adapter. */
  unitShipping: number
  quantity: number
}

/** 'quoted' = the dealer typed the figure on a quote; the policy below never ran. */
export type ShippingReason =
  | 'empty' | 'set_freight' | 'accessory_flat' | 'free_ach' | 'pickup' | 'quoted' | 'beyond_continental'

export interface ShippingBreakdownItem {
  id: string
  kind: ShippingItemKind
  quantity: number
  /** This line's real freight cost (unitShipping × quantity). */
  freightCost: number
}

export interface ShippingResult {
  /** What the customer pays. */
  charged: number
  /** Dealer's real freight obligation, independent of `charged`. */
  freightCost: number
  reason: ShippingReason
  breakdown: ShippingBreakdownItem[]
}

export interface ComputeShippingOptions {
  /**
   * True when the customer chose to pay by bank (ACH) AND the dealer enabled
   * free-ACH shipping. Re-derived server-side from the settled payment method —
   * never trusted from a client claim. Zeroes `charged`, leaves `freightCost`.
   */
  freeShippingApplied?: boolean
  /**
   * Store pickup (migration 387): nothing is shipped, so the customer pays 0
   * AND the dealer bears no freight. Wins over `freeShippingApplied`.
   */
  pickup?: boolean
  /**
   * The destination is outside the continental US (BUSINESS INPUT, Timm
   * 2026-09-17: the rates below cover the lower 48 only). Nothing is charged
   * at checkout — the dealer quotes the real freight and bills it before the
   * order ships, so the buyer is never charged a rate that does not apply.
   * Pickup still wins; free-ACH does not (the waive covers our rate, not
   * freight we have not priced yet).
   */
  beyondContinentalUs?: boolean
  /** Flat charge for an accessory-only cart. Defaults to FLAT_ACCESSORY_RATE. */
  flatAccessoryRate?: number
}

// BUSINESS INPUT (confirmed Timm 2026-09-17): the states and countries the
// shipping rates do NOT cover. Alaska and Hawaii, the US territories and
// military mail (AA/AE/AP), and every country other than the US.
const BEYOND_CONTINENTAL_US_STATES = new Set(['AK', 'HI', 'AS', 'GU', 'MP', 'PR', 'VI', 'AA', 'AE', 'AP'])

/** True when an address is outside the continental US (the lower 48 + DC), so
 *  the shipping rates do not cover it. An address with no state yet counts as
 *  continental — the checkout has nothing to warn about until one is picked. */
export function shipsBeyondContinentalUs(address: { country: string; state?: string | null }): boolean {
  if (address.country.trim().toUpperCase() !== 'US') return true
  return BEYOND_CONTINENTAL_US_STATES.has((address.state ?? '').trim().toUpperCase())
}

// BUSINESS INPUT (confirmed Timm 2026-05-29; made the DEFAULT 2026-09-25): the
// flat shipping charged on an accessory-only order (no eFoil set in the cart,
// consolidated single box) when the store has not set its own. Each dealer can
// now set their store's rate — any amount >= 0, $0 = free — in
// storefront_settings.flat_accessory_shipping (migration 400); a blank setting
// means this default. Only this rate is per-store: set freight, free-ACH,
// pickup and beyond-continental keep their precedence over it.
export const FLAT_ACCESSORY_RATE = 30

/** The largest accessory rate a store may set: Stripe's USD ceiling
 *  (99,999,999 cents). Mirrors migration 400's CHECK. */
export const MAX_FLAT_ACCESSORY_RATE = 999_999.99

/**
 * A store's stored accessory rate (storefront_settings.flat_accessory_shipping,
 * dollars) → the `flatAccessoryRate` to pass computeShipping, or undefined for
 * "not set" (computeShipping then uses FLAT_ACCESSORY_RATE). A value that is
 * not a finite number in [0, MAX_FLAT_ACCESSORY_RATE] is treated as not set — the column's CHECK already
 * refuses one, so this only keeps a malformed read from ever pricing an order.
 * Rounded to cents; accepts a numeric string in case a driver returns one.
 */
export function storeFlatAccessoryRate(stored: number | string | null | undefined): number | undefined {
  if (stored == null || stored === '') return undefined
  const n = typeof stored === 'number' ? stored : Number(stored)
  if (!Number.isFinite(n) || n < 0 || n > MAX_FLAT_ACCESSORY_RATE) return undefined
  return Math.round(n * 100) / 100
}

// BUSINESS INPUT (confirmed Timm 2026-05-29): the freight charged per eFoil set.
// The server is authoritative — it charges each set line's `unitShipping`
// (products.default_shipping), which migration 208 sets to this value for every
// set. This constant is the canonical rate for client-side preview (the cart
// drawer, which has no per-SKU freight on hand) and the migration's target.
// computeShipping itself never reads it — freight always flows through
// `unitShipping` so a future per-dealer rate stays data-driven.
export const SET_SHIPPING_RATE = 300

/**
 * Map a product's catalog category / type to a shipping kind. eFoil sets
 * (product_type 'bundle', or category 'efoil_set') are 'set'. Everything else
 * is 'accessory'. Pure so the checkout adapter and the webhook adapter classify
 * identically (parity across the three inlined copies).
 */
export function shippingKindFor(input: { category?: string | null; productType?: string | null }): ShippingItemKind {
  if (input.productType === 'bundle' || input.category === 'efoil_set') return 'set'
  return 'accessory'
}

const roundCents = (n: number): number => Math.round(n * 100) / 100

/**
 * Compute the customer charge and the dealer's real freight cost for an order.
 * See the file header for the policy.
 */
export function computeShipping(items: ShippingInput[], opts: ComputeShippingOptions = {}): ShippingResult {
  const flatAccessoryRate = opts.flatAccessoryRate ?? FLAT_ACCESSORY_RATE

  const breakdown: ShippingBreakdownItem[] = items.map((i) => ({
    id: i.id,
    kind: i.kind,
    quantity: i.quantity,
    freightCost: roundCents(i.unitShipping * i.quantity),
  }))

  // Dealer's real freight obligation — always the sum of real per-line freight.
  const freightCost = roundCents(breakdown.reduce((sum, b) => sum + b.freightCost, 0))

  if (items.length === 0) {
    return { charged: 0, freightCost: 0, reason: 'empty', breakdown }
  }

  // Store pickup: nothing travels — no charge and no freight obligation.
  if (opts.pickup) {
    return { charged: 0, freightCost: 0, reason: 'pickup', breakdown: breakdown.map((b) => ({ ...b, freightCost: 0 })) }
  }

  // Beyond the continental US our rates do not apply: charge nothing now and
  // let the dealer quote the real freight. freightCost stays — the dealer's
  // own obligation is unchanged by where the charge lands.
  if (opts.beyondContinentalUs) {
    return { charged: 0, freightCost, reason: 'beyond_continental', breakdown }
  }

  // Free-ACH override: customer pays $0, dealer still bears
  // freightCost (the absorbed amount the insight panel surfaces).
  if (opts.freeShippingApplied) {
    return { charged: 0, freightCost, reason: 'free_ach', breakdown }
  }

  const setFreight = roundCents(
    breakdown.filter((b) => b.kind === 'set').reduce((sum, b) => sum + b.freightCost, 0),
  )

  // Any set in the cart → charge set freight ($300 × set qty); accessories ride free.
  if (breakdown.some((b) => b.kind === 'set')) {
    return { charged: setFreight, freightCost, reason: 'set_freight', breakdown }
  }

  // Accessory-only cart → flat consolidated rate.
  return { charged: flatAccessoryRate, freightCost, reason: 'accessory_flat', breakdown }
}
